/**
 * Responsive Design Utilities for Lobby2.0
 * Mobile-first responsive mixins and utilities
 */

import type { CSSObject } from 'styled-components';
import { css } from 'styled-components';
import { theme } from './theme';

// Type for responsive properties
type ResponsiveCSS = CSSObject | string;

// Mobile-first breakpoint utilities
export const breakpoints = {
  xs: (styles: ResponsiveCSS) => css`
    @media (max-width: 320px) {
      ${styles}
    }
  `,
  xs1: (styles: ResponsiveCSS) => css`
    @media (max-width: 375px) {
      ${styles}
    }
  `,
  xs2: (styles: ResponsiveCSS) => css`
    @media (max-width: 390px) {
      ${styles}
    }
  `,
  xs3: (styles: ResponsiveCSS) => css`
    @media (max-width: 414px) {
      ${styles}
    }
  `,
  xs4: (styles: ResponsiveCSS) => css`
    @media (max-width: 430px) {
      ${styles}
    }
  `,
  sm: (styles: ResponsiveCSS) => css`
    @media (max-width: 576px) {
      ${styles}
    }
  `,
  md: (styles: ResponsiveCSS) => css`
    @media (max-width: 768px) {
      ${styles}
    }
  `,
  lg: (styles: ResponsiveCSS) => css`
    @media (max-width: 1025px) {
      ${styles}
    }
  `,
  xl: (styles: ResponsiveCSS) => css`
    @media (max-width: 1366px) {
      ${styles}
    }
  `,
  
  // Min-width queries for mobile-first approach
  smUp: (styles: ResponsiveCSS) => css`
    @media (min-width: 577px) {
      ${styles}
    }
  `,
  mdUp: (styles: ResponsiveCSS) => css`
    @media (min-width: 769px) {
      ${styles}
    }
  `,
  lgUp: (styles: ResponsiveCSS) => css`
    @media (min-width: 1026px) {
      ${styles}
    }
  `,
  xlUp: (styles: ResponsiveCSS) => css`
    @media (min-width: 1367px) {
      ${styles}
    }
  `,
  
  // Orientation queries
  portrait: (styles: ResponsiveCSS) => css`
    @media (orientation: portrait) {
      ${styles}
    }
  `,
  landscape: (styles: ResponsiveCSS) => css`
    @media (orientation: landscape) {
      ${styles}
    }
  `,
  
  // Touch device detection
  touch: (styles: ResponsiveCSS) => css`
    @media (hover: none) and (pointer: coarse) {
      ${styles}
    }
  `,
  hover: (styles: ResponsiveCSS) => css`
    @media (hover: hover) and (pointer: fine) {
      ${styles}
    }
  `,
  
  // High DPI displays (Retina)
  retina: (styles: ResponsiveCSS) => css`
    @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
      ${styles}
    }
  `,
};

// Touch optimization mixin
export const touchOptimized = css`
  -webkit-tap-highlight-color: ${theme.touch.ios.tapHighlightColor};
  -webkit-touch-callout: ${theme.touch.ios.touchCallout};
  -webkit-user-select: ${theme.touch.ios.userSelect};
  -moz-user-select: ${theme.touch.ios.userSelect};
  -ms-user-select: ${theme.touch.ios.userSelect};
  user-select: ${theme.touch.ios.userSelect};
  -webkit-overflow-scrolling: ${theme.touch.ios.overflowScrolling};
  overscroll-behavior: ${theme.touch.ios.overscrollBehavior};
`;

// Smooth scrolling mixin
export const smoothScrolling = css`
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  
  // Custom scrollbar for mobile
  &::-webkit-scrollbar {
    width: 4px;
    height: 4px;
    
    ${breakpoints.mdUp(`
      width: 8px;
      height: 8px;
    `)}
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 2px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 2px;
    
    &:hover {
      background: rgba(255, 255, 255, 0.5);
    }
  }
`;
